import { formatPhone } from '@provider-directory/components/phone-number';
import { PROVIDER_SCHEDULING_TYPES } from '@provider-directory/constants';
import { type FeatureFlags } from '@provider-directory/types/flags';
import { ClientOrgContextKey } from '@provider-directory/types/organizations.contracts';
import {
  ProviderLocationResource,
  ProviderResource,
  ProviderDetailsResource,
  ProviderSpecialtyResource,
  Status,
  AppointmentProvider,
} from '@provider-directory/types/providers.contracts';
import type { AssociationPhoneSelection } from '@provider-directory/types/runtime-config.contracts';

/**
 * Returns the location flagged as primary, falling back to the first location
 * if none is explicitly marked. Returns undefined for an empty array.
 */
export const getPrimaryLocation = <T extends { isPrimaryLocation: boolean }>(
  locations: T[],
): T | undefined =>
  locations.find((location) => location.isPrimaryLocation) ?? locations[0];

export const getProviderPrimaryPhone = (
  providerLocations: ProviderLocationResource[],
): string | null => {
  const primaryPhone = providerLocations
    .find((location) => location.isPrimaryLocation)
    ?.contacts.find((contact) => contact.type === 'phone')?.value;
  // formatPhone can strip a truthy-but-junk value (e.g. "call office") down to
  // ''; return null so getPhoneNumber's `??` chain reaches the association fallback.
  const formatted = primaryPhone ? formatPhone(primaryPhone) : null;
  return formatted || null;
};

export const getProviderLocationPhoneById = (
  providerLocations: ProviderLocationResource[],
  locationId: string,
): string | null => {
  const location = providerLocations.find(
    (loc) => loc.ehrExternalId === locationId,
  );
  const phone = location?.contacts.find(
    (contact) => contact.type === 'phone',
  )?.value;
  return phone ? formatPhone(phone) : null;
};

/**
 * Gets the primary specialty of a provider.
 * Falls back to the first one if no primary one is found.
 */
export const getProviderPrimarySpecialty = (
  provider: { specialties: ProviderSpecialtyResource[] } | null,
): ProviderSpecialtyResource | undefined => {
  if (!provider) return undefined;

  return (
    provider.specialties.find((specialty) => specialty.isPrimary) ??
    provider.specialties[0]
  );
};

/**
 * Returns true if the provider should be routed through the V2
 * Primary Care / Pediatrics decision tree.
 *
 * A provider qualifies when ANY of the following is true:
 * 1. The backend marks them as `isPrimaryCare`.
 * 2. They have a specialty literally named "Primary Care".
 * 3. Their primary specialty maps to the `pediatrics` branch in the
 *    tenant's `specialtyMap`.
 *
 * Keep this in sync across all decision-tree consumers (do NOT inline
 * the checks) — see `getDecisionTreeSpecialty`, `useSchedulingDecision`
 * and `shouldGoToMyChart`.
 */
export const isPrimaryCarePediatricsProvider = (
  provider: ProviderResource,
  specialtyMap: Record<string, string>,
): boolean => {
  const primarySpecialty =
    getProviderPrimarySpecialty(provider)?.name.toLowerCase() ?? '';
  return (
    provider.isPrimaryCare ||
    provider.specialties.some((s) => s.name === 'Primary Care') ||
    specialtyMap[primarySpecialty] === 'pediatrics'
  );
};

/**
 * Determines whether a provider should be filtered (hidden) for the given
 * organization context.
 *
 * `contextFilterMapping` declares which association codes grant a provider
 * visibility within a directory; a provider carrying none of them is filtered
 * out. An absent entry means no filtering for that directory.
 */
export const shouldFilterProviderForContext = (
  provider: ProviderResource,
  contextKey: ClientOrgContextKey,
  contextFilterMapping?: Record<string, string[]>,
): boolean => {
  const requiredCodes = contextFilterMapping?.[contextKey];
  // No filter declared for this directory ⇒ nothing is hidden.
  if (!requiredCodes || requiredCodes.length === 0) {
    return false;
  }
  return !provider.hospitalAssociations.some((h) =>
    requiredCodes.includes(h.code),
  );
};

/**
 * Determines whether a provider belongs exclusively to a named association
 * group: it qualifies if ALL its association codes appear in that group.
 * `associationGroupCodes` is the only source — an undeclared group matches
 * nobody.
 */
export const hasOnlyGroupAssociation = (
  provider: ProviderResource | ProviderDetailsResource | null | undefined,
  groupName: string,
  associationGroupCodes?: Record<string, string[]>,
): boolean => {
  if (!provider?.hospitalAssociations.length) {
    return false;
  }

  const groupCodes = associationGroupCodes?.[groupName];
  // Group not declared ⇒ nobody belongs to it.
  if (!groupCodes || groupCodes.length === 0) {
    return false;
  }
  const groupSet = new Set(groupCodes);
  return provider.hospitalAssociations.every((h) => groupSet.has(h.code));
};

const hasAssociation = (provider: ProviderResource, code: string): boolean =>
  provider.hospitalAssociations.some((h) => h.code === code);

const resolveAssociationPhone = (
  provider: ProviderResource,
  selection?: AssociationPhoneSelection,
): string | undefined => {
  if (!selection || !selection.enabled) return undefined;

  const isEmployed = provider.status === Status.enum.Employed;

  for (const code of selection.precedence) {
    if (!hasAssociation(provider, code)) continue;
    if (!(code in selection.byHospitalAssociation)) continue;
    const entry = selection.byHospitalAssociation[code];
    if (entry.displayNumberByStatus) {
      return isEmployed
        ? entry.displayNumberByStatus.employed
        : entry.displayNumberByStatus.affiliate;
    }
    return entry.displayNumber;
  }
  return undefined;
};

export const getPhoneNumber = (
  provider: ProviderResource,
  selection?: AssociationPhoneSelection,
): string | null => {
  return (
    getProviderPrimaryPhone(provider.locations) ??
    resolveAssociationPhone(provider, selection) ??
    null
  );
};

export const shouldGoToMyChart = ({
  provider,
  specialtyMap,
  featureFlags,
}: {
  provider: ProviderResource;
  specialtyMap: Record<string, string>;
  featureFlags: FeatureFlags;
}): boolean => {
  const isPrimaryCarePediatrics = isPrimaryCarePediatricsProvider(
    provider,
    specialtyMap,
  );
  const primarySpecialty =
    getProviderPrimarySpecialty(provider)?.name.toLowerCase() ?? '';
  const isCardiology = specialtyMap[primarySpecialty] === 'cardiology';
  const isDermatology = specialtyMap[primarySpecialty] === 'dermatology';
  const isAllergy = specialtyMap[primarySpecialty] === 'allergy';
  const isOrthopedics = specialtyMap[primarySpecialty] === 'orthopedics';
  const isEndocrinology = specialtyMap[primarySpecialty] === 'endocrinology';
  const isGastroenterology =
    specialtyMap[primarySpecialty] === 'gastroenterology';
  const isOnlineFabricScheduling =
    provider.scheduling === PROVIDER_SCHEDULING_TYPES.OnlineFabric;
  const {
    isAllergyDecisionTreeEnabled,
    isOrthoDecisionTreeEnabled,
    isEndocrinologyDecisionTreeEnabled,
    isGastroenterologyDecisionTreeEnabled,
    isPrimaryCareTreeEnabled,
  } = featureFlags;

  // Redirect if provider does not match any known decision-tree branch
  // but has online fabric scheduling
  if (
    !isPrimaryCarePediatrics &&
    !isCardiology &&
    !isDermatology &&
    !isAllergy &&
    !isOrthopedics &&
    !isEndocrinology &&
    !isGastroenterology &&
    isOnlineFabricScheduling
  ) {
    return true;
  }

  // Primary Care tree (V2)
  if (isPrimaryCarePediatrics) {
    if (isPrimaryCareTreeEnabled) return false;
    return true;
  }
  // Allergy logic
  if (isAllergy) {
    if (isAllergyDecisionTreeEnabled) return false;
    return true;
  }

  if (isOrthopedics) {
    if (isOrthoDecisionTreeEnabled) return false;
    return true;
  }

  if (isEndocrinology) {
    if (isEndocrinologyDecisionTreeEnabled) return false;
    return true;
  }

  if (isGastroenterology) {
    if (isGastroenterologyDecisionTreeEnabled) return false;
    return true;
  }

  return false;
};

export const isSchedulingMyChart = ({
  provider,
  specialtyMap,
  featureFlags,
}: {
  provider: ProviderResource;
  specialtyMap: Record<string, string>;
  featureFlags: FeatureFlags;
}): boolean => {
  const isOpenScheduling =
    provider.scheduling === PROVIDER_SCHEDULING_TYPES.OnlineOpenScheduling;
  const redirectToMyChart = shouldGoToMyChart({
    provider,
    specialtyMap,
    featureFlags,
  });

  return isOpenScheduling || redirectToMyChart;
};

export const findVisitTypeIdByRfv = (
  provider: ProviderResource | ProviderDetailsResource | AppointmentProvider,
  locationId: string,
  reasonForVisit: string,
): string | null => {
  const location = provider?.locations.find(
    (loc) => loc.ehrExternalId === locationId,
  );
  if (!location) return null;

  const selectedRFV = location.reasonsForVisit.find(
    (rfv) => rfv.id === reasonForVisit,
  );
  return selectedRFV ? selectedRFV.ehrVisitTypeId : null;
};
